import { useRef, useState } from 'react';

import type { ChatAttachment } from '../../types/chat.types';
import { ChatAttachmentAcceptUtil } from '../../utils/chat-attachment-accept-util';

import { useSparkStatus } from './SparkStatusProvider';

/**
 * useSparkAttachments - I own the composer's file shelf. ONLY concerned with the files waiting to
 * be sent - no uploading, no sending, and none of the drag mechanics, which are `FileIntake`'s.
 */
export const useSparkAttachments = () => {
  const { setNotice } = useSparkStatus();

  const [attachments, setAttachments] = useState<ChatAttachment[]>([]);

  const attachmentsRef = useRef<ChatAttachment[]>([]);

  const putAttachments = (next: ChatAttachment[]) => {
    attachmentsRef.current = next;
    setAttachments(next);
  };

  const announceRejected = ({ rejected }: { rejected: string[] }) => {
    if (rejected.length === 0) return;

    setNotice(
      rejected.length === 1
        ? `${rejected[0]} is not a file type Spark can read.`
        : `${rejected.length} files were not attached - Spark cannot read those file types.`
    );
  };

  const addDroppedFiles = ({
    accepted,
    rejected,
  }: {
    accepted: File[];
    rejected: string[];
  }) => {
    announceRejected({ rejected });

    if (accepted.length === 0) return;

    putAttachments([
      ...attachmentsRef.current,
      ...accepted.map(file => ({ file, id: crypto.randomUUID() })),
    ]);
  };

  const addAttachments = (files: FileList | null) => {
    if (!files) return;

    const { accepted, rejected } = ChatAttachmentAcceptUtil.partition({
      files,
    });

    announceRejected({ rejected });

    if (accepted.length === 0) return;

    putAttachments([...attachmentsRef.current, ...accepted]);
  };

  /*
    By id, not by index. A remove and a drop can land in either order, and an index
    captured in a chip's closure names a different file the moment anything ahead of it
    leaves the shelf.
  */
  const removeAttachment = (attachmentId: ChatAttachment['id']) => {
    putAttachments(
      attachmentsRef.current.filter(
        attachment => attachment.id !== attachmentId
      )
    );
  };

  /** Emptying the shelf outright - what "New Chat" does alongside resetting the composer. */
  const clearAttachments = () => {
    putAttachments([]);
  };

  return {
    addAttachments,
    addDroppedFiles,
    attachments,
    attachmentsRef,
    clearAttachments,
    putAttachments,
    removeAttachment,
  };
};
